/**
 * The application. Story VC-016.
 *
 * Three screens and a sheet. There is no router: three states and one
 * transition each does not need one, and a router would be a dependency
 * whose job is to turn a string into the same `switch` that is written here.
 *
 * # Configuration is in the URL
 *
 * The two-port development setup the README describes serves this page and
 * the runtime on different origins, so the endpoint cannot be derived from
 * `location` alone — hence `?endpoint=`. Everything else derives from it, so
 * a developer types one parameter rather than four.
 *
 * A deployment is the same shape for a different reason: `deployment_lld.md`
 * §4.4 gives the page one name and the runtime another, and the bundle is
 * built knowing the second (`VITE_API_ORIGIN`, §8.5). A page that derived it
 * from `location` would call itself (VC-019).
 *
 * The credential comes from the identity provider when one is configured
 * (VC-017): the page redirects to its hosted pages, and no password ever
 * reaches this code. `?token=` remains for the development path, where no
 * provider is configured — a published credential in a browser history,
 * which is exactly why a deployment does not run that way.
 */

import { useCallback, useEffect, useMemo, useState } from "react";

import { useAuth } from "./auth/useAuth.js";

import { createSession } from "./api/runtime.js";
import { debugCredential } from "./debug/credential.js";
import { DebugSheet } from "./components/DebugSheet.jsx";
import { Icon, Mark } from "./components/Icon.jsx";
import { Call } from "./screens/Call.jsx";
import { Chat } from "./screens/Chat.jsx";
import { Home } from "./screens/Home.jsx";
import { rememberSession } from "./session/store.js";
import { resolveEndpoints } from "./settings/endpoints.js";

export function App() {
  const settings = useMemo(readSettings, []);
  // VC-017. `static` when no provider is configured, which is what keeps
  // `scripts/start.sh` working on a machine with no AWS account.
  const auth = useAuth(import.meta.env, settings.config.token);
  // The credential the screens carry. Derived rather than stored, so a
  // refresh lands in every call that has not been made yet.
  const config = useMemo(
    () => ({ ...settings.config, token: auth.accessToken }),
    [settings.config, auth.accessToken],
  );
  const [screen, setScreen] = useState({ name: "home" });
  const [inspecting, setInspecting] = useState(null);
  const [makeDecoder, setMakeDecoder] = useState(null);
  const [problem, setProblem] = useState(null);
  const [starting, setStarting] = useState(false);

  // The SSE framing, from `agent-sse`. What is shared is the
  // *factory*: a decoder holds the bytes of a partial event, so each stream
  // makes its own. Imported lazily so the home screen still renders on a
  // deployment whose `pkg/` was never built.
  useEffect(() => {
    let cancelled = false;
    import("../pkg/sse/agent_sse.js")
      .then(async (wasm) => {
        await wasm.default();
        if (!cancelled) {
          // Two arrows: `useState`'s setter calls a function it is given,
          // so storing one needs the extra wrapper.
          setMakeDecoder(() => () => new wasm.WasmTextEventStream());
        }
      })
      .catch(() => {
        if (!cancelled) {
          setProblem(
            "the client bundle is missing. Run scripts/build.sh — it builds " +
              "the WebAssembly this page reads the event stream with.",
          );
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const start = useCallback(
    async (kind) => {
      setProblem(null);
      setStarting(true);
      try {
        const created = await createSession(config);
        rememberSession(created.session_id, {
          kind,
          agent: config.agent,
        });
        // One session, two doors (`voice_agent_hld_v2.md` §17). A call is
        // the *same* conversation a chat would have been, created the same
        // way and then joined over the socket — which is what makes
        // "pick up where you left off" work across both.
        setScreen(
          kind === "call"
            ? { name: "call", sessionId: created.session_id }
            : { name: "chat", sessionId: created.session_id, turns: [] },
        );
      } catch (error) {
        setProblem(`could not open a session: ${error.message}`);
      } finally {
        setStarting(false);
      }
    },
    [config],
  );

  /**
   * Picks a conversation up where it was left — by typing into it.
   *
   * Including one that was a call, and that is the protocol's own answer
   * rather than a shortcut. `voice_wire_protocol.md` §10 resumes a *voice*
   * session only inside a fifteen-second grace window, and says in as many
   * words what happens after it: "the runtime session and its conversation
   * survive, so a consumer can offer continuity at the application level;
   * the protocol does not do it implicitly." This is that offer. A new call
   * would be a new conversation, which is the thing the home screen exists
   * to avoid.
   *
   * Nothing is carried over from this browser but the id. VC-016 seeded the
   * screen here with the user's remembered turns and left the agent's half
   * to §40.3's resume, which rendered as every question followed by every
   * answer — the two lists had no key in common to interleave by. §40.9's
   * transcript is now the single source for both halves and
   * `useConversation` reads it, so there is nothing for this to pass.
   */
  const resume = useCallback((session) => {
    setScreen({ name: "chat", sessionId: session.id, turns: [] });
  }, []);

  const home = useCallback(() => setScreen({ name: "home" }), []);

  // Nothing behind the login is rendered while a redirect is in flight.
  // A home screen that appeared and then vanished would teach a user the
  // page is unreliable, and its buttons would call an API with no token.
  if (auth.state === "signing-in") {
    return (
      <div className="app">
        <p className="status" role="status">
          {auth.problem ? (
            <>
              <Icon name="warning" size={14} /> {auth.problem}
            </>
          ) : (
            "Signing in…"
          )}
        </p>
      </div>
    );
  }

  return (
    <div className="app">
      {problem ? (
        <p className="status status--error" role="alert">
          <Icon name="warning" size={14} /> {problem}
        </p>
      ) : null}

      {screen.name === "home" ? (
        <Home
          agent={config.agent}
          callBlocked={settings.callBlocked}
          onStart={start}
          onResume={resume}
          onInspect={setInspecting}
        />
      ) : null}

      {screen.name === "call" && makeDecoder ? (
        <Call
          config={config}
          endpoint={settings.endpoint}
          sessionId={screen.sessionId}
          makeDecoder={makeDecoder}
          onHome={home}
          onInspect={() => setInspecting(screen.sessionId)}
        />
      ) : null}

      {screen.name === "chat" && makeDecoder ? (
        <Chat
          config={config}
          sessionId={screen.sessionId}
          makeDecoder={makeDecoder}
          initialTurns={screen.turns}
          onHome={home}
          onInspect={() => setInspecting(screen.sessionId)}
        />
      ) : null}

      {starting ? (
        <div className="splash">
          <Mark size={56} />
          <p className="status">opening a session…</p>
        </div>
      ) : null}

      {inspecting ? (
        <DebugSheet
          sessionId={inspecting}
          config={config}
          debugToken={debugCredential({
            debugToken: settings.debugToken,
            sessionToken: config.token,
          })}
          onClose={() => setInspecting(null)}
        />
      ) : null}
    </div>
  );
}

/**
 * The page's configuration, from the query string and the bundle.
 *
 * Where the runtime is has its own module and its own suite, because the
 * answer has three sources and this function cannot be called without a
 * browser — see `settings/endpoints.js`. Everything else here is a default.
 */
function readSettings() {
  const params = new URLSearchParams(location.search);
  const { endpoint, base, tenant, agent } = resolveEndpoints(
    location.search,
    location.origin,
    import.meta.env,
  );
  const token = params.get("token") ?? "dev-token";

  return {
    endpoint,
    // §30.7's second shared secret, which only a *static* deployment has.
    // Absent by default, and `debugCredential` is what decides what goes in
    // its place — under an identity provider the credential is the session's
    // own and the grant is a group (VC-022).
    debugToken: params.get("debug_token") ?? "",
    config: {
      base,
      token,
      tenant,
      agent,
    },
    // Why a call cannot be started here, or `null` when it can. Checked
    // before the button is offered rather than after it is pressed: "Call"
    // that fails on tap teaches a user the product is broken.
    //
    // Two reasons and not one boolean, because the second is the one people
    // actually hit and it is fixable: a page opened over plain HTTP on a
    // LAN address is not a secure context, and no browser will hand it a
    // microphone. "Needs a microphone on this device" would be a lie in
    // front of a device that has one.
    callBlocked: callBlockedReason(),
  };
}

/** @returns {string | null} */
function callBlockedReason() {
  if (typeof navigator === "undefined" || !navigator.mediaDevices?.getUserMedia) {
    return "This browser has no microphone API.";
  }
  if (!(globalThis.isSecureContext ?? false)) {
    return "Needs https, or localhost. A browser gives no microphone to a page served over plain http.";
  }
  return null;
}
